import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  docData,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  writeBatch,
  query,
  where,
} from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import { Servicio, ServicioFrecuencia } from '../models/servicio.model';

@Injectable({ providedIn: 'root' })
export class ServicioService {
  private firestore = inject(Firestore);

  private col(residencialId: string) {
    return collection(this.firestore, `residenciales/${residencialId}/servicios`);
  }

  private ref(residencialId: string, servicioId: string) {
    return doc(this.firestore, `residenciales/${residencialId}/servicios/${servicioId}`);
  }

  // ── Lecturas ─────────────────────────────────────────────────────────────────

  getAll(residencialId: string): Observable<Servicio[]> {
    return collectionData(this.col(residencialId), { idField: 'id' }) as Observable<Servicio[]>;
  }

  // Lectura puntual — sin listener. Usada una vez por sesión para cargar catálogos en páginas de residente.
  async getAllOnce(residencialId: string): Promise<Servicio[]> {
    const snap = await getDocs(query(this.col(residencialId), where('activo', '==', true)));
    return snap.docs.map(d => ({ id: d.id, ...d.data() } as Servicio));
  }

  getById(residencialId: string, servicioId: string): Observable<Servicio> {
    return docData(this.ref(residencialId, servicioId), { idField: 'id' }) as Observable<Servicio>;
  }

  // ── Escritura ─────────────────────────────────────────────────────────────────

  async crear(residencialId: string, datos: Omit<Servicio, 'id'>): Promise<string> {
    try {
      const docRef = await addDoc(this.col(residencialId), datos);
      return docRef.id;
    } catch (e) {
      throw new Error(`Error al crear servicio: ${(e as Error).message}`);
    }
  }

  async actualizar(
    residencialId: string,
    servicioId: string,
    cambios: Partial<Omit<Servicio, 'id'>>,
    estadoActual?: Omit<Servicio, 'id'>
  ): Promise<void> {
    try {
      const ref = this.ref(residencialId, servicioId);

      let actual: Omit<Servicio, 'id'>;
      if (estadoActual) {
        actual = estadoActual;
      } else {
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error(`Servicio ${servicioId} no encontrado`);
        actual = snap.data() as Omit<Servicio, 'id'>;
      }

      const nuevoMonto       = cambios.monto       ?? actual.monto;
      const nuevoEsMontoFijo = cambios.esMontoFijo ?? actual.esMontoFijo;

      // Propagar si el resultado es fijo Y (acaba de volverse fijo O cambió el monto)
      const debePropagarMonto =
        nuevoEsMontoFijo && (!actual.esMontoFijo || nuevoMonto !== actual.monto);

      const payload: Partial<Omit<Servicio, 'id'>> = { ...cambios };

      if (debePropagarMonto) {
        const baseEntries = cambios.residentesAsociados ?? actual.residentesAsociados ?? [];
        payload.residentesAsociados = baseEntries.map(entry => ({ ...entry, monto: nuevoMonto }));
      }

      await updateDoc(ref, payload);
    } catch (e) {
      throw new Error(`Error al actualizar servicio: ${(e as Error).message}`);
    }
  }

  async desactivar(residencialId: string, servicioId: string): Promise<void> {
    try {
      await updateDoc(this.ref(residencialId, servicioId), { activo: false });
    } catch (e) {
      throw new Error(`Error al desactivar servicio: ${(e as Error).message}`);
    }
  }

  async quitarResidenteDeServicios(
    residencialId: string,
    residenteUid: string,
    servicios: Servicio[]
  ): Promise<void> {
    const afectados = servicios.filter(s =>
      s.residentesAsociados?.some(e => e.residenteUid === residenteUid)
    );
    if (afectados.length === 0) return;

    try {
      const batch = writeBatch(this.firestore);
      for (const s of afectados) {
        batch.update(this.ref(residencialId, s.id), {
          residentesAsociados: s.residentesAsociados.filter(e => e.residenteUid !== residenteUid),
        });
      }
      await batch.commit();
    } catch (e) {
      throw new Error(`Error al quitar residente de servicios: ${(e as Error).message}`);
    }
  }

  // ── Helpers de periodo (puros) ────────────────────────────────────────────────

  getPeriodoDeFecha(frecuencia: ServicioFrecuencia, fecha: Date): string {
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');

    if (frecuencia === 'mensual') {
      return `${y}-${m}`;
    } else if (frecuencia === 'quincenal') {
      const q = fecha.getDate() <= 15 ? 'Q1' : 'Q2';
      return `${y}-${m}-${q}`;
    } else if (frecuencia === 'semanal') {
      // ISO 8601: Thursday of the week determines year and week number
      const thu = new Date(fecha);
      thu.setHours(0, 0, 0, 0);
      thu.setDate(thu.getDate() + 4 - (thu.getDay() || 7));
      const jan1    = new Date(thu.getFullYear(), 0, 1);
      const week    = Math.ceil(((thu.getTime() - jan1.getTime()) / 86400000 + 1) / 7);
      const isoYear = thu.getFullYear();
      return `${isoYear}-W${String(week).padStart(2, '0')}`;
    } else {
      return `${y}-${m}-${d}`;
    }
  }

  getPeriodoSiguiente(frecuencia: ServicioFrecuencia, fecha: Date): string {
    if (frecuencia === 'mensual') {
      return this.getPeriodoDeFecha('mensual', new Date(fecha.getFullYear(), fecha.getMonth() + 1, 1));
    } else if (frecuencia === 'quincenal') {
      const next = fecha.getDate() <= 15
        ? new Date(fecha.getFullYear(), fecha.getMonth(), 16)      // Q1 → Q2
        : new Date(fecha.getFullYear(), fecha.getMonth() + 1, 1);  // Q2 → Q1 mes siguiente
      return this.getPeriodoDeFecha('quincenal', next);
    } else if (frecuencia === 'semanal') {
      const next = new Date(fecha.getTime() + 7 * 24 * 60 * 60 * 1000);
      return this.getPeriodoDeFecha('semanal', next);
    } else {
      // unica — el caller no debe invocar getPeriodoSiguiente para esta frecuencia
      return this.getPeriodoDeFecha('unica', fecha);
    }
  }

}
